import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, History, Film, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";

const kpiConfig = [
  { key: "draft", label: "Draft", color: "border-l-gray-400 text-gray-700" },
  { key: "generating", label: "Generating", color: "border-l-yellow-500 text-yellow-700" },
  { key: "ready", label: "Ready", color: "border-l-emerald-500 text-emerald-700" },
  { key: "published", label: "Published", color: "border-l-[#7C3AED] text-[#7C3AED]" },
];

const VFDashboard = () => {
  const [data, setData] = useState({ kpi: {}, videos: [] });
  const [deletingId, setDeletingId] = useState(null);

  const load = () => api.get("/video-factory/dashboard").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleDelete = async (e, v) => {
    e.preventDefault();
    e.stopPropagation();
    const label = v.title || v.topic || "this video";
    if (!confirm(`Delete "${label}"? This permanently removes the draft, voiceover, and rendered files.`)) return;
    setDeletingId(v.id);
    try {
      await api.delete(`/video-factory/${v.id}`);
      // Refresh dashboard so KPIs also update
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to delete video");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="AI Video Factory"
        subtitle="Create complete videos from just a topic — 100% automated."
        testId="vf-dashboard-header"
        actions={
          <div className="flex gap-2">
            <Link to="/video-factory/history" className="op-btn-secondary"><History size={16} /> History</Link>
            <Link to="/video-factory/new" className="op-btn-primary" data-testid="vf-new-video"><Plus size={16} /> New Video</Link>
          </div>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {kpiConfig.map((k) => (
          <div key={k.key} className={`op-card p-5 border-l-4 ${k.color.split(' ')[0]}`} data-testid={`vf-kpi-${k.key}`}>
            <p className="text-xs font-semibold text-gray-500 uppercase">{k.label}</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{data.kpi[k.key] || 0}</p>
          </div>
        ))}
      </div>
      <h2 className="text-lg font-bold text-gray-900 mb-4">Recent Videos</h2>
      {data.videos.length === 0 ? (
        <div className="op-card"><EmptyState icon={Film} title="No videos yet" description="Generate your first AI video — script, voice, and visuals included." action={<Link to="/video-factory/new" className="op-btn-primary">Create Video →</Link>} /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {data.videos.map((v) => (
            <div key={v.id} className="op-card overflow-hidden hover:border-[#7C3AED] relative group" data-testid={`vf-video-${v.id}`}>
              <Link to={`/video-factory/new/${v.id}`} className="block">
                <div className="aspect-video bg-gray-100 relative">
                  {v.thumbnails?.[0] ? <img src={v.thumbnails[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-300"><Film size={36} /></div>}
                  <span className={`absolute top-2 right-2 px-2 py-0.5 text-xs font-semibold rounded-full ${v.status === 'ready' ? 'bg-emerald-500 text-white' : v.status === 'published' ? 'bg-[#7C3AED] text-white' : 'bg-gray-200 text-gray-700'}`}>{v.status}</span>
                </div>
                <div className="p-3 pr-10">
                  <p className="text-sm font-medium text-gray-900 truncate">{v.title || v.topic}</p>
                  <p className="text-xs text-gray-500 mt-1">{(v.platforms || []).slice(0, 3).join(", ")}</p>
                </div>
              </Link>
              <button
                type="button"
                onClick={(e) => handleDelete(e, v)}
                disabled={deletingId === v.id}
                title="Delete video"
                aria-label="Delete video"
                className="absolute bottom-2 right-2 p-1.5 rounded-md bg-white/90 backdrop-blur border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-300 disabled:opacity-50 transition"
                data-testid={`vf-delete-${v.id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </PageContainer>
  );
};
export default VFDashboard;
